import { useMutation } from '@tanstack/react-query'
import { useToken } from '@/src/store/token-store'
import { useUser } from '@/src/store/user-store'
import { User } from '@/src/types/user'
import { makeApiRequest } from '@/src/lib/api-request'
import { getBrowserName, getDeviceType } from '@/src/lib/utils'
import { toast } from 'sonner'

interface ValidateOtpMutationParams {
  verifyCode: string
}

export const useValidateOtpMutation = () => {
  const { setUser } = useUser()
  return useMutation({
    mutationKey: ['user', 'verify-otp'],
    mutationFn: async (params: ValidateOtpMutationParams) => {
      const response = await makeApiRequest({
        method: 'POST',
        url: '/Login_New/otpValidate',
        data: {
          verifyCode: params.verifyCode,
          type: 'email',
        },
        isAuthenticated: true,
        fallbackError: 'Error while verifying verification code',
        isV3Api: true,
      })

      setUser(response.response as User)

      return response
    },
  })
}

export const useValidateOtpMutationForgotPassword = () => {
  const { setToken } = useToken()
  const { setRefreshToken, setUser } = useUser()
  return useMutation({
    mutationKey: ['user', 'verify-otp-forgot-password'],
    mutationFn: async (params: { token: string; otp: string }) => {
      const response = await makeApiRequest({
        method: 'POST',
        url: '/Login_New/forgotPasswordOtpValidate',
        data: {
          token: params.token,
          otp: params.otp,
        },
        isAuthenticated: false,
        fallbackError: 'Error while verifying verification code',
        isV3Api: true,
      })

      setToken(response.token)
      setRefreshToken(response.refresh_token)
      setUser(response.response as User)

      return response.response as User
    },
  })
}

export const useResendOtpMutation = () => {
  return useMutation({
    mutationKey: ['user', 'resend-otp'],
    mutationFn: async (params?: { cfToken?: string }) => {
      const challengeToken =
        params?.cfToken || localStorage.getItem('cft-resendOtp') || ''

      await makeApiRequest({
        method: 'GET',
        url: '/Login_New/resendOtp',
        isAuthenticated: true,
        fallbackError: 'Error while resending verification code',
        data: {},
        headers: {
          'X-USR-DEVICE': getDeviceType(),
          'X-USR-BROWSER': getBrowserName(),
          'x-api-chlng': challengeToken,
        },
        isV3Api: true,
      })
    },
  })
}

export const useResendPwaOtpMutation = () => {
  return useMutation({
    mutationKey: ['user', 'user-resen-otp-pwa'],
    mutationFn: async (params: { email: string }) => {
      const response = await makeApiRequest({
        method: 'POST',
        url: '/Login_New/googleAppleLogin_PWA',
        data: {
          email: params.email,
        },
        isAuthenticated: false,
        fallbackError: 'Error while sending verification code',
        isV3Api: true,
      })
       return  response 
    },
  })
}
export const useValidatePwaOtpMutation = () => {
  const { setToken } = useToken()
  const { setRefreshToken, setUser } = useUser()
  return useMutation({
    mutationKey: ['user', 'verify-pwa-otp'],
    mutationFn: async (params: { email: string; verifyCode: string }) => {
      const response = await makeApiRequest({
        method: 'POST',
        url: '/Login_New/otpValidate_PWA',
        data: {
          email: params.email,
          verifyCode: params.verifyCode,
        },
        isAuthenticated: false,
        fallbackError: 'Error while verifying verification code',
        isV3Api: true,
      })
      setToken(response.token)
      setRefreshToken(response.refresh_token)
      setUser(response.response as User)
      toast.success('Logged in successfully')
    },
  })
}

export const useValidateTvOtpMutation = () => {
  return useMutation({
    mutationKey: ['verify-tv-otp'],
    mutationFn: async (params: { verifyCode: number }) => {
      const response = await makeApiRequest({
        method: 'POST',
        url: '/tv/otp_validate',
        data: {
          otp: params.verifyCode,
        },
        isAuthenticated: true,
        fallbackError: 'noFallback',
        isV3Api: true,
      })
      return response
    },
  })
}